import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Badge from './Badge';
import { useConfirm, useToast } from '../context/FeedbackContext';

// Keep in step with ALLOWED_EXT / MAX_MB in server/routes/documents.js.
const ALLOWED_EXT = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt'];
const ACCEPT_TYPES = ALLOWED_EXT.map(e => '.' + e).join(',');
const MAX_MB = 25;
const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'];
const VIEWABLE_EXT = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'];

function fileExt(filename) {
  const name = filename || '';
  return name.includes('.') ? name.split('.').pop().toLowerCase() : '';
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = (new Date(dateStr) - new Date());
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function fmtSize(bytes) {
  if (!bytes) return '';
  return bytes > 1048576 ? (bytes / 1048576).toFixed(1) + ' MB' : (bytes / 1024).toFixed(0) + ' KB';
}

function getFileIcon(filename) {
  const ext = fileExt(filename);
  if (ext === 'pdf') return '📄';
  if (IMAGE_EXT.includes(ext)) return '🖼';
  if (['doc', 'docx', 'txt'].includes(ext)) return '📝';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
  return '📎';
}

// Only PDFs and browser-renderable images open in the viewer; the rest
// (Word, Excel, HEIC, text) are download-only.
function isViewable(filename) {
  return VIEWABLE_EXT.includes(fileExt(filename));
}

function ExpiryBadge({ dateStr }) {
  const days = daysUntil(dateStr);
  if (days === null) return null;
  if (days < 0) return <Badge variant="danger">Expired {Math.abs(days)}d ago</Badge>;
  if (days <= 30) return <Badge variant="warning">Expires in {days}d</Badge>;
  if (days <= 60) return <Badge variant="info">Expires in {days}d</Badge>;
  return null;
}

export default function DocumentWallet({ entityType, entityId, entityName = '' }) {
  const confirm = useConfirm();
  const toast = useToast();
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [needsExpiryTypes, setNeedsExpiryTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [viewDoc, setViewDoc] = useState(null);
  const [viewUrl, setViewUrl] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const emptyForm = useCallback(() => ({
    doc_type: '', custom_type: '', title: '', issue_date: '', expiry_date: '', notes: '', file: null,
  }), []);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(() => {
    if (!entityType || !entityId) return;
    setLoading(true);
    setError(false);
    Promise.all([
      api.get('/documents', { params: { entity_type: entityType, entity_id: entityId } }),
      api.get(`/documents/doc-types/${entityType}`, { params: { entity_id: entityId } })
    ])
      .then(([docsRes, typesRes]) => {
        setDocs(docsRes.data);
        setDocTypes(typesRes.data.types || []);
        setNeedsExpiryTypes(typesRes.data.needsExpiry || []);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  function resetForm() {
    setForm(emptyForm());
    setUploadError('');
  }

  function effectiveType(f) {
    return f.doc_type === 'Other' ? f.custom_type : f.doc_type;
  }

  function needsExpiry(f) {
    return needsExpiryTypes.includes(effectiveType(f));
  }

  function updateDocType(value) {
    setForm(f => {
      const autoTitle = value === 'Other' ? '' : (entityName ? `${value} - ${entityName}` : value);
      return { ...f, doc_type: value, custom_type: '', title: autoTitle };
    });
  }

  function updateCustomType(value) {
    setForm(f => ({ ...f, custom_type: value, title: value ? (entityName ? `${value} - ${entityName}` : value) : '' }));
  }

  async function handleUpload() {
    const type = effectiveType(form);
    if (!form.title || !form.file) {
      setUploadError('Title and file are required.');
      return;
    }
    if (form.doc_type === 'Other' && !form.custom_type.trim()) {
      setUploadError('Custom type name is required when "Other" is selected.');
      return;
    }
    if (type && type.trim().length > 60) {
      setUploadError('Custom type name must be 60 characters or fewer.');
      return;
    }
    const file = form.file;
    if (!ALLOWED_EXT.includes(fileExt(file.name))) {
      setUploadError(`${file.name} is not an allowed file type. Allowed: ${ALLOWED_EXT.join(', ').toUpperCase()}.`);
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setUploadError(`${file.name} is ${(file.size / 1048576).toFixed(1)} MB; the limit is ${MAX_MB} MB.`);
      return;
    }
    setSaving(true);
    setUploadError('');
    try {
      const fd = new FormData();
      fd.append('entity_type', entityType);
      fd.append('entity_id', entityId);
      fd.append('title', form.title);
      if (type) fd.append('doc_type', type);
      if (form.issue_date) fd.append('issue_date', form.issue_date);
      if (form.expiry_date) fd.append('expiry_date', form.expiry_date);
      if (form.notes) fd.append('notes', form.notes);
      fd.append('file', form.file);

      await api.post('/documents', fd);
      resetForm();
      setShowForm(false);
      load();
    } catch (e) {
      const status = e.response?.status;
      const serverMsg = e.response?.data?.error;
      if (serverMsg) setUploadError(serverMsg);
      else if (status === 413) setUploadError(`${file.name} is too large for the server.`);
      else if (status) setUploadError(`Upload failed (HTTP ${status}) for ${file.name}.`);
      else setUploadError(`Upload failed for ${file.name}: ${e.message || 'network error'}.`);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(doc) {
    if (!(await confirm({ title: 'Delete document?', message: `"${doc.title}" will be permanently deleted. This cannot be undone.`, confirmLabel: 'Delete', danger: true }))) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      load();
    } catch (e) {
      toast(e.response?.data?.error || 'Delete failed.', 'error');
    }
  }

  async function handleDownload(doc) {
    if (doc.file_path && doc.file_path.startsWith('http')) {
      window.open(doc.file_path, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const response = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || doc.title;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast('Download failed.', 'error');
    }
  }

  async function handleView(doc) {
    if (doc.file_path && doc.file_path.startsWith('http')) {
      window.open(doc.file_path, '_blank', 'noopener,noreferrer');
      return;
    }
    setViewDoc(doc);
    setViewUrl(null);
    setViewLoading(true);
    try {
      const response = await api.get(`/documents/${doc.id}/view`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      setViewUrl(window.URL.createObjectURL(blob));
    } catch (e) {
      toast('Could not load document preview.', 'error');
    }
    setViewLoading(false);
  }

  function closeViewer() {
    if (viewUrl) window.URL.revokeObjectURL(viewUrl);
    setViewDoc(null);
    setViewUrl(null);
  }

  const viewExt = viewDoc ? fileExt(viewDoc.file_name) : '';
  const viewIsImage = IMAGE_EXT.includes(viewExt);

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h4 style={{ fontSize: 13, fontWeight: 700, margin: 0 }}>📁 Documents</h4>
        <button className="btn btn-secondary btn-sm" onClick={() => { resetForm(); setShowForm(s => !s); }}>
          {showForm ? 'Cancel' : '+ Upload'}
        </button>
      </div>

      {showForm && (
        <div style={{ marginBottom: 16, padding: 12, background: 'var(--surface)', borderRadius: 'var(--radius)' }}>
          <div className="form-field" style={{ marginBottom: 10 }}>
            <label>Document Type</label>
            <select value={form.doc_type} onChange={e => updateDocType(e.target.value)}>
              <option value="">— Select —</option>
              {docTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {form.doc_type === 'Other' && (
            <div className="form-field" style={{ marginBottom: 10 }}>
              <label>Custom Type Name</label>
              <input
                type="text"
                value={form.custom_type}
                onChange={e => updateCustomType(e.target.value)}
                placeholder="e.g. Employment Contract"
              />
            </div>
          )}

          <div className="form-field" style={{ marginBottom: 10 }}>
            <label>Title</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Auto-filled from type — edit if needed"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div className="form-field">
              <label>Issue Date</label>
              <input
                type="date"
                value={form.issue_date}
                onChange={e => setForm(f => ({ ...f, issue_date: e.target.value }))}
              />
            </div>
            <div className="form-field">
              <label>Expiry Date {needsExpiry(form) && <span style={{ color: 'var(--danger-text)' }}>*</span>}</label>
              <input
                type="date"
                value={form.expiry_date}
                onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))}
              />
            </div>
          </div>

          <div className="form-field" style={{ marginBottom: 10 }}>
            <label>Notes</label>
            <input
              type="text"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            />
          </div>

          <div className="form-field" style={{ marginBottom: 10 }}>
            <label>File (PDF, image, Word, Excel, CSV or text; max {MAX_MB} MB)</label>
            <input
              type="file"
              accept={ACCEPT_TYPES}
              onChange={e => setForm(f => ({ ...f, file: e.target.files[0] || null }))}
            />
          </div>

          {uploadError && (
            <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '8px 10px', borderRadius: 'var(--radius)', fontSize: 12, marginBottom: 10 }}>{uploadError}</div>
          )}
          <button className="btn btn-primary btn-sm" onClick={handleUpload} disabled={saving}>
            {saving ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      )}

      {loading && <div className="form-hint">Loading documents…</div>}
      {error && <div className="form-hint">Failed to load documents.</div>}
      {!loading && !error && docs.length === 0 && (
        <div className="form-hint">No documents uploaded yet.</div>
      )}

      {!loading && !error && docs.map(doc => {
        const days = daysUntil(doc.expiry_date);
        const isExpired = days !== null && days < 0;
        const expiringSoon = days !== null && days >= 0 && days <= 30;
        return (
          <div
            key={doc.id}
            style={{
              background: isExpired ? 'var(--danger-bg)' : 'var(--surface)',
              border: isExpired ? '1px solid var(--danger-text)' : undefined,
              borderRadius: 'var(--radius)', padding: 10, marginBottom: 8, fontSize: 12,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', gap: 8, minWidth: 0 }}>
                <div style={{ fontSize: 18, flexShrink: 0 }}>{getFileIcon(doc.file_name)}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{doc.title}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                    {doc.doc_type && <Badge variant="info">{doc.doc_type}</Badge>}
                    <ExpiryBadge dateStr={doc.expiry_date} />
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {isViewable(doc.file_name) && (
                  <button className="btn btn-ghost btn-sm" title="View" onClick={() => handleView(doc)}>👁</button>
                )}
                <button className="btn btn-ghost btn-sm" title="Download" onClick={() => handleDownload(doc)}>⬇</button>
                {user?.role === 'admin' && (
                  <button className="btn btn-ghost btn-sm" title="Delete" onClick={() => handleDelete(doc)}>🗑</button>
                )}
              </div>
            </div>
            {doc.notes && <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 6 }}>{doc.notes}</div>}
            <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 4 }}>
              {doc.file_name}{doc.file_size ? ` (${fmtSize(doc.file_size)})` : ''}
              {doc.issue_date && ` · Issued: ${doc.issue_date}`}
              {doc.expiry_date && ` · Expires: ${doc.expiry_date}`}
            </div>
          </div>
        );
      })}

      {viewDoc && (
        <div className="panel-overlay" onClick={closeViewer}>
          <div
            style={{
              position: 'fixed', top: '5%', left: '10%', right: '10%', bottom: '5%',
              background: 'var(--card)', borderRadius: 'var(--radius-lg)', display: 'flex', flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)', zIndex: 1000,
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontWeight: 700 }}>{viewDoc.title}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{viewDoc.file_name} {viewDoc.file_size ? `· ${fmtSize(viewDoc.file_size)}` : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => handleDownload(viewDoc)}>⬇ Download</button>
                <button className="drawer-close" onClick={closeViewer}>×</button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0' }}>
              {viewLoading && <div style={{ color: 'var(--muted)' }}>Loading document…</div>}
              {!viewLoading && viewUrl && viewIsImage && (
                <img src={viewUrl} alt={viewDoc.title} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              )}
              {!viewLoading && viewUrl && !viewIsImage && (
                <iframe src={viewUrl} title={viewDoc.title} style={{ width: '100%', height: '100%', border: 'none' }} />
              )}
              {!viewLoading && !viewUrl && (
                <div style={{ textAlign: 'center', color: 'var(--muted)' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
                  <div>Could not load preview</div>
                  <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => handleDownload(viewDoc)}>Download Instead</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
